import React, { useEffect, useState } from 'react';
import { ref, get } from 'firebase/database';
import { X, CheckCircle2, Megaphone, Clock, AlertTriangle } from 'lucide-react';
import { db } from '../../../../integrations/firebase';
import { ChannelService } from '../../services/ChannelService';

const fmt = (ms) => ms ? new Date(ms).toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit',
}) : '';

/** Tell the chat list (and anything else listening) that an acknowledgement changed. */
export const notifyAckChanged = () => { try { window.dispatchEvent(new Event('channel-ack-changed')); } catch { /* ignore */ } };

/**
 * Under a message that asks for acknowledgement.
 *
 * Acknowledging is an explicit button press — reading the message, a read receipt or an
 * emoji never counts. Readers see their own state; the author and channel admins see the
 * running count and can open the full list.
 */
export const AckBar = ({ chatId, msg, uid, canSeeStatus, isMine }) => {
    const [ackedAt, setAckedAt] = useState(undefined);      // undefined = still checking
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [showStatus, setShowStatus] = useState(false);
    const overdue = !!msg.ackDeadline && Date.now() > msg.ackDeadline;

    useEffect(() => {
        if (isMine || !uid) { setAckedAt(null); return; }
        let live = true;
        get(ref(db, `channelAcks/${chatId}/${msg.id}/${uid}`))
            .then(s => { if (live) setAckedAt(typeof s.val() === 'number' ? s.val() : null); })
            .catch(() => { if (live) setAckedAt(null); });
        return () => { live = false; };
    }, [chatId, msg.id, uid, isMine]);

    const acknowledge = async () => {
        setBusy(true); setError(null);
        try {
            const r = await ChannelService.acknowledge(chatId, msg.id);
            setAckedAt(r.ackedAt);
            notifyAckChanged();
        } catch (e) { setError(e.message); } finally { setBusy(false); }
    };

    return (
        <div className={`flex ${isMine ? 'justify-end' : 'justify-start'} -mt-1 mb-2`}>
            <div className="flex flex-wrap items-center gap-2 text-[11px] bg-amber-50 border border-amber-100 rounded-xl px-3 py-1.5 max-w-[85%]">
                <span className={`flex items-center gap-1 font-semibold ${overdue ? 'text-red-600' : 'text-amber-700'}`}>
                    {overdue ? <AlertTriangle size={12} /> : <Clock size={12} />}
                    Acknowledge by {fmt(msg.ackDeadline)}
                </span>
                {!isMine && ackedAt === null && (
                    <button onClick={acknowledge} disabled={busy}
                        className="font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 rounded-lg px-2.5 py-1">
                        {busy ? 'Saving…' : 'I acknowledge'}
                    </button>
                )}
                {!isMine && typeof ackedAt === 'number' && (
                    <span className="flex items-center gap-1 text-emerald-700 font-semibold">
                        <CheckCircle2 size={12} /> Acknowledged {fmt(ackedAt)}
                    </span>
                )}
                {canSeeStatus && (
                    <button onClick={() => setShowStatus(true)} className="font-semibold text-indigo-600 hover:underline">
                        Who has acknowledged?
                    </button>
                )}
                {error && <span className="text-red-600" role="alert">{error}</span>}
            </div>
            {showStatus && <AckStatusModal chatId={chatId} messageId={msg.id} onClose={() => setShowStatus(false)} />}
        </div>
    );
};

/** The author's / admins' view: who has acknowledged, who has not, and whether it is overdue. */
export const AckStatusModal = ({ chatId, messageId, onClose }) => {
    const [s, setS] = useState(null);
    const [error, setError] = useState(null);
    useEffect(() => {
        ChannelService.ackStatus(chatId, messageId).then(setS).catch(e => setError(e.message));
    }, [chatId, messageId]);

    return (
        <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
            <div role="dialog" aria-modal="true" aria-label="Acknowledgements" className="w-full max-w-sm max-h-[80vh] flex flex-col bg-white rounded-2xl shadow-2xl" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
                    <h3 className="text-sm font-bold text-slate-800">Acknowledgements</h3>
                    <button onClick={onClose} className="p-1 rounded-full text-slate-400 hover:bg-slate-100" aria-label="Close"><X size={16} /></button>
                </div>
                <div className="overflow-y-auto custom-scrollbar p-4 text-sm">
                    {error && <p className="text-red-600 text-xs" role="alert">{error}</p>}
                    {!s && !error && <p className="text-slate-400 text-xs">Loading…</p>}
                    {s && (
                        <>
                            <p className={`text-xs font-semibold mb-3 ${s.overdue ? 'text-red-600' : 'text-slate-600'}`}>
                                {s.acked.length} of {s.total} acknowledged
                                {s.deadline ? ` · deadline ${fmt(s.deadline)}` : ''}
                                {s.overdue ? ' · OVERDUE' : ''}
                            </p>
                            {s.pending.length > 0 && (
                                <>
                                    <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-1">Not yet</p>
                                    <ul className="mb-3">{s.pending.map(p => <li key={p.uid} className="py-1 text-slate-700">{p.name}</li>)}</ul>
                                </>
                            )}
                            {s.acked.length > 0 && (
                                <>
                                    <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-1">Acknowledged</p>
                                    <ul>{s.acked.map(a => (
                                        <li key={a.uid} className="py-1 flex justify-between gap-3 text-slate-700">
                                            <span className="truncate">{a.name}</span>
                                            <span className={`text-[11px] flex-shrink-0 ${s.deadline && a.at > s.deadline ? 'text-red-500' : 'text-slate-400'}`}>
                                                {fmt(a.at)}{s.deadline && a.at > s.deadline ? ' (late)' : ''}
                                            </span>
                                        </li>
                                    ))}</ul>
                                </>
                            )}
                            <p className="text-[10px] text-slate-400 mt-3">Counted against the channel's current members.</p>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

/** Channel admins: post a notice, optionally requiring acknowledgement by a deadline. */
export const AnnounceModal = ({ chatId, channelName, onClose, onPosted }) => {
    const [text, setText] = useState('');
    const [needAck, setNeedAck] = useState(true);
    const [deadline, setDeadline] = useState(() => {
        // Default: two days from now, 6 pm IST, in the <input type="datetime-local"> format.
        // en-CA formats as YYYY-MM-DD; the zone makes it the IST calendar date.
        const day = new Date(Date.now() + 2 * 864e5).toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
        return `${day}T18:00`;
    });
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true); setError(null);
        try {
            // The input has no zone; the business runs on IST, so read it as IST explicitly
            // rather than as whatever timezone this browser happens to be set to.
            const at = needAck ? Date.parse(`${deadline}:00+05:30`) : undefined;
            await ChannelService.announce(chatId, text, at);
            notifyAckChanged();
            onPosted?.();
            onClose?.();
        } catch (err) { setError(err.message); } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
            <form role="dialog" aria-modal="true" aria-label="New notice" onSubmit={submit} onClick={e => e.stopPropagation()}
                className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-5 grid gap-3">
                <div className="flex items-center justify-between">
                    <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2"><Megaphone size={16} className="text-indigo-500" /> Notice to #{channelName}</h3>
                    <button type="button" onClick={onClose} className="p-1 rounded-full text-slate-400 hover:bg-slate-100" aria-label="Close"><X size={16} /></button>
                </div>
                <textarea required autoFocus rows={5} maxLength={4000} value={text} onChange={e => setText(e.target.value)}
                    placeholder="Write the notice…" aria-label="Notice text"
                    className="rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 resize-none" />
                <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input type="checkbox" checked={needAck} onChange={e => setNeedAck(e.target.checked)} />
                    Everyone must acknowledge it
                </label>
                {needAck && (
                    <label className="grid gap-1 text-xs font-semibold text-slate-600">
                        Deadline (IST)
                        <input type="datetime-local" required value={deadline} onChange={e => setDeadline(e.target.value)}
                            className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-normal" />
                    </label>
                )}
                {error && <p className="text-xs text-red-600" role="alert">{error}</p>}
                <button type="submit" disabled={busy || !text.trim()} className="rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-sm font-semibold py-2.5">
                    {busy ? 'Posting…' : 'Post notice'}
                </button>
            </form>
        </div>
    );
};
